/**
 * CustomerCardFooter
 *
 * Dumb Save/Cancel chrome for the customer card. Purely presentational — renders the action
 * button(s) and reports clicks upward. `onCancel` is optional: when omitted (e.g. embedded flows
 * with no slideout to close) only the primary Apply button is rendered.
 *
 * `variant="search"` swaps the chrome for the business-search phase: the primary button reads
 * "Continue" instead of the configured `saveLabel`, and `onSave`/`disabled` carry the
 * continue-business-conversion handler/gating instead of apply.
 */
import { Button } from '@makemydeal/dr-ui-bricks-components';
import { Spinner } from '@makemydeal/ui-bricks/dist/cox';

import {
    StyledCustomerCardFooter,
    StyledCancelButton,
    StyledSaveButtonWrapper,
    StyledSaveButtonContent,
    StyledSaveButtonLabel,
    StyledSaveButtonSpinner
} from './CustomerInformationExperience.style';

// `buttonSize="small"` + `width="auto"` size the ui-bricks buttons to their content so the footer
// keeps the compact look instead of the default 170px width.
//
// The ui-bricks Button does not spread its rest props, so `data-testid` and `aria-busy` never reach
// the DOM (it does forward `className`, so the buttons carry a matching class to stay selectable);
// `aria-busy` is declared on the wrapper element around the save button instead.
export const CustomerInformationFooter: React.FC<{
    onSave: () => void;
    onCancel?: () => void;
    disabled?: boolean;
    cancelDisabled?: boolean;
    /** Label for the primary button — e.g. "Apply" in the slideout, "Save" on financing. Ignored when `variant="search"`. */
    saveLabel?: string;
    /** When true, the primary button shows an in-flight spinner in place of its label and stays disabled. */
    saving?: boolean;
    /** 'default' (Cancel/Apply, the standard footer) or 'search' (Cancel/Continue, business-search phase). */
    variant?: 'default' | 'search';
}> = ({ onSave, onCancel, disabled = false, cancelDisabled = false, saveLabel = 'Apply', saving = false, variant = 'default' }) => {
    const primaryLabel = variant === 'search' ? 'Continue' : saveLabel;

    return (
        <StyledCustomerCardFooter>
            {onCancel && (
                <StyledCancelButton
                    data-testid="customer-card-cancel-button"
                    className="customer-card-cancel-button"
                    buttonStyle="tertiary"
                    buttonSize="small"
                    width="auto"
                    onClick={onCancel}
                    disabled={cancelDisabled}
                >
                    Cancel
                </StyledCancelButton>
            )}
            <StyledSaveButtonWrapper aria-busy={saving || undefined}>
                <Button
                    data-testid="customer-card-save-button"
                    className="customer-card-save-button"
                    buttonStyle="primary"
                    buttonSize="small"
                    width="auto"
                    onClick={onSave}
                    disabled={disabled || saving}
                >
                    <StyledSaveButtonContent>
                        <StyledSaveButtonLabel $hidden={saving}>{primaryLabel}</StyledSaveButtonLabel>
                        {saving && (
                            <StyledSaveButtonSpinner>
                                <Spinner size={16} />
                            </StyledSaveButtonSpinner>
                        )}
                    </StyledSaveButtonContent>
                </Button>
            </StyledSaveButtonWrapper>
        </StyledCustomerCardFooter>
    );
};

export default CustomerInformationFooter;
